/**
 * Theme Toggle Component
 * Sun/Moon icon toggle for switching between light and dark themes
 */

import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { transitions, borderRadius } from '../design-system/tokens';

interface ThemeToggleProps {
  className?: string;
}

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: ${borderRadius.md};
  background: ${props =>
    props.theme?.colors?.button?.secondary || 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  cursor: pointer;
  transition: ${transitions.all};

  &:hover {
    background: ${props =>
      props.theme?.colors?.button?.secondaryHover || 'rgba(255, 255, 255, 0.2)'};
    color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

// Sun icon for light mode
const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"></circle>
    <line x1="12" y1="1" x2="12" y2="3"></line>
    <line x1="12" y1="21" x2="12" y2="23"></line>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
    <line x1="1" y1="12" x2="3" y2="12"></line>
    <line x1="21" y1="12" x2="23" y2="12"></line>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
  </svg>
);

// Moon icon for dark mode
const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <ToggleButton
      onClick={toggleTheme}
      className={className}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? <SunIcon /> : <MoonIcon />}
    </ToggleButton>
  );
};

export default ThemeToggle;
